import { useState, useEffect } from 'react';
import {
  TrendingUp,
  TrendingDown,
  DollarSign,
  Package,
  ShoppingCart,
  Users,
  AlertTriangle,
  RefreshCw,
  Calendar,
  CreditCard,
  MapPin,
  TrendingDown as Minus
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend
} from 'recharts';

// Colores para gráficos
const COLORS = {
  primary: '#3b82f6',
  secondary: '#10b981',
  accent: '#f59e0b',
  danger: '#ef4444',
  purple: '#8b5cf6',
  pink: '#ec4899'
};

const CHART_COLORS = [
  COLORS.primary,
  COLORS.secondary,
  COLORS.accent,
  COLORS.danger,
  COLORS.purple,
  COLORS.pink
];

interface DashboardStats {
  revenue: {
    current: number;
    previous: number;
    change: number;
  };
  orders: {
    current: number;
    previous: number;
    change: number;
    pending: number;
  };
  customers: {
    current: number;
    previous: number;
    change: number;
    new_today: number;
  };
  avg_order_value: {
    current: number;
    previous: number;
    change: number;
  };
  products_low_stock: number;
}

interface SalesTimelineData {
  period: string;
  total_sales: number;
  order_count: number;
  avg_order_value: number;
}

interface TopProduct {
  product_id: string;
  product_name: string;
  total_quantity: number;
  total_revenue: number;
  order_count: number;
}

interface CategorySales {
  category_id: string;
  category_name: string;
  total_revenue: number;
  product_count: number;
  order_count: number;
}

interface DepartmentSales {
  department: string;
  total_revenue: number;
  order_count: number;
  avg_order_value: number;
}

interface PaymentMetrics {
  payment_method: string;
  total_orders: number;
  total_revenue: number;
  avg_order_value: number;
  percentage: number;
}

export default function DashboardTabNew() {
  const [timeRange, setTimeRange] = useState<7 | 30 | 365>(30);
  const [isLoading, setIsLoading] = useState(false);
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [salesTimeline, setSalesTimeline] = useState<SalesTimelineData[]>([]);
  const [topProducts, setTopProducts] = useState<TopProduct[]>([]);
  const [categorySales, setCategorySales] = useState<CategorySales[]>([]);
  const [departmentSales, setDepartmentSales] = useState<DepartmentSales[]>([]);
  const [paymentMetrics, setPaymentMetrics] = useState<PaymentMetrics[]>([]);

  const formatCurrency = (amount: number) => {
    return `Q ${amount.toLocaleString('es-GT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatChange = (change: number) => {
    const sign = change >= 0 ? '+' : '';
    return `${sign}${change.toFixed(1)}%`;
  };

  // Cargar estadísticas generales
  const loadStats = async () => {
    try {
      const { data, error } = await supabase.rpc('get_dashboard_stats', {
        time_range_days: timeRange
      });

      if (error) throw error;
      setStats(data);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  // Cargar línea de tiempo de ventas
  const loadSalesTimeline = async () => {
    try {
      const { data, error } = await supabase.rpc('get_sales_timeline', {
        time_range_days: timeRange,
        group_by_month: timeRange === 365
      });

      if (error) throw error;
      setSalesTimeline(data || []);
    } catch (error) {
      console.error('Error loading sales timeline:', error);
    }
  };

  // Cargar top productos
  const loadTopProducts = async () => {
    try {
      const { data, error } = await supabase.rpc('get_top_products', {
        time_range_days: timeRange,
        limit_count: 10
      });

      if (error) throw error;
      setTopProducts(data || []);
    } catch (error) {
      console.error('Error loading top products:', error);
    }
  };

  // Cargar ventas por categoría
  const loadCategorySales = async () => {
    try {
      const { data, error } = await supabase.rpc('get_sales_by_category', {
        time_range_days: timeRange
      });

      if (error) throw error;
      setCategorySales(data || []);
    } catch (error) {
      console.error('Error loading category sales:', error);
    }
  };

  // Cargar ventas por departamento
  const loadDepartmentSales = async () => {
    try {
      const { data, error } = await supabase.rpc('get_sales_by_department', {
        time_range_days: timeRange
      });

      if (error) throw error;
      setDepartmentSales(data || []);
    } catch (error) {
      console.error('Error loading department sales:', error);
    }
  };

  // Cargar métricas de pago
  const loadPaymentMetrics = async () => {
    try {
      const { data, error } = await supabase.rpc('get_payment_metrics', {
        time_range_days: timeRange
      });

      if (error) throw error;
      setPaymentMetrics(data || []);
    } catch (error) {
      console.error('Error loading payment metrics:', error);
    }
  };

  // Cargar todos los datos
  const loadAllData = async () => {
    setIsLoading(true);
    try {
      await Promise.all([
        loadStats(),
        loadSalesTimeline(),
        loadTopProducts(),
        loadCategorySales(),
        loadDepartmentSales(),
        loadPaymentMetrics()
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadAllData();
  }, [timeRange]);

  if (!stats) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Dashboard Analítico</h1>
            <p className="text-gray-500 mt-1">Métricas y estadísticas del negocio</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setTimeRange(7)}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                  timeRange === 7 ? 'bg-white shadow-sm text-blue-600' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                7 días
              </button>
              <button
                onClick={() => setTimeRange(30)}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                  timeRange === 30 ? 'bg-white shadow-sm text-blue-600' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                30 días
              </button>
              <button
                onClick={() => setTimeRange(365)}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                  timeRange === 365 ? 'bg-white shadow-sm text-blue-600' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                1 año
              </button>
            </div>
            <button
              onClick={loadAllData}
              disabled={isLoading}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
              <span className="text-sm font-medium">Actualizar</span>
            </button>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Revenue Card */}
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-lg p-6 text-white">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-white/20 rounded-lg">
              <DollarSign className="w-6 h-6" />
            </div>
            <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
              stats.revenue.change >= 0 ? 'bg-green-500/20' : 'bg-red-500/20'
            }`}>
              {stats.revenue.change >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
              {formatChange(stats.revenue.change)}
            </div>
          </div>
          <h3 className="text-2xl font-bold">{formatCurrency(stats.revenue.current)}</h3>
          <p className="text-blue-100 text-sm mt-1">Ingresos Totales</p>
        </div>

        {/* Orders Card */}
        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl shadow-lg p-6 text-white">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-white/20 rounded-lg">
              <ShoppingCart className="w-6 h-6" />
            </div>
            <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
              stats.orders.change >= 0 ? 'bg-green-500/20' : 'bg-red-500/20'
            }`}>
              {stats.orders.change >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
              {formatChange(stats.orders.change)}
            </div>
          </div>
          <h3 className="text-2xl font-bold">{stats.orders.current}</h3>
          <p className="text-green-100 text-sm mt-1">
            Pedidos ({stats.orders.pending} pendientes)
          </p>
        </div>

        {/* Customers Card */}
        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl shadow-lg p-6 text-white">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-white/20 rounded-lg">
              <Users className="w-6 h-6" />
            </div>
            <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
              stats.customers.change >= 0 ? 'bg-green-500/20' : 'bg-red-500/20'
            }`}>
              {stats.customers.change >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
              {formatChange(stats.customers.change)}
            </div>
          </div>
          <h3 className="text-2xl font-bold">{stats.customers.current}</h3>
          <p className="text-purple-100 text-sm mt-1">
            Clientes ({stats.customers.new_today} nuevos hoy)
          </p>
        </div>

        {/* AOV Card */}
        <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl shadow-lg p-6 text-white">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-white/20 rounded-lg">
              <TrendingUp className="w-6 h-6" />
            </div>
            <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
              stats.avg_order_value.change >= 0 ? 'bg-green-500/20' : 'bg-red-500/20'
            }`}>
              {stats.avg_order_value.change >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
              {formatChange(stats.avg_order_value.change)}
            </div>
          </div>
          <h3 className="text-2xl font-bold">{formatCurrency(stats.avg_order_value.current)}</h3>
          <p className="text-orange-100 text-sm mt-1">Valor Promedio del Pedido</p>
        </div>
      </div>

      {/* Alertas */}
      {stats.products_low_stock > 0 && (
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-lg">
          <div className="flex items-center">
            <AlertTriangle className="w-5 h-5 text-yellow-600 mr-3" />
            <p className="text-sm text-yellow-700">
              <span className="font-medium">{stats.products_low_stock} productos</span> con inventario bajo
            </p>
          </div>
        </div>
      )}

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Sales Timeline */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Calendar className="w-5 h-5 text-blue-600" />
            Tendencia de Ventas
          </h3>
          <div className="h-80">
            {salesTimeline.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={salesTimeline}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="period" tick={{ fontSize: 12 }} />
                  <YAxis yAxisId="left" tick={{ fontSize: 12 }} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 12 }} />
                  <Tooltip
                    formatter={(value: number, name: string) => {
                      if (name === 'total_sales') return [formatCurrency(value), 'Ventas'];
                      if (name === 'order_count') return [value, 'Pedidos'];
                      return [formatCurrency(value), 'Promedio'];
                    }}
                    contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                  />
                  <Legend />
                  <Line
                    yAxisId="left"
                    type="monotone"
                    dataKey="total_sales"
                    name="Ventas"
                    stroke={COLORS.primary}
                    strokeWidth={3}
                    dot={{ fill: COLORS.primary, strokeWidth: 2, r: 4 }}
                    activeDot={{ r: 6 }}
                  />
                  <Line
                    yAxisId="right"
                    type="monotone"
                    dataKey="order_count"
                    name="Pedidos"
                    stroke={COLORS.secondary}
                    strokeWidth={2}
                    dot={{ fill: COLORS.secondary, strokeWidth: 2, r: 3 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-gray-400">
                No hay datos disponibles
              </div>
            )}
          </div>
        </div>

        {/* Payment Methods */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-green-600" />
            Métodos de Pago
          </h3>
          <div className="h-80">
            {paymentMetrics.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={paymentMetrics}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ payment_method, percentage }) => `${payment_method} (${percentage.toFixed(1)}%)`}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="total_revenue"
                  >
                    {paymentMetrics.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(value: number) => formatCurrency(value)}
                    contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-gray-400">
                No hay datos disponibles
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Charts Row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Products */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Package className="w-5 h-5 text-purple-600" />
            Productos Más Vendidos
          </h3>
          <div className="space-y-3">
            {topProducts.slice(0, 5).map((product, index) => (
              <div key={product.product_id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                <div className="flex-shrink-0 w-8 h-8 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-white font-bold text-sm">
                  {index + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900 truncate">{product.product_name}</p>
                  <p className="text-sm text-gray-500">
                    {product.total_quantity} vendidos · {product.order_count} pedidos
                  </p>
                </div>
                <div className="flex-shrink-0">
                  <p className="font-semibold text-gray-900">{formatCurrency(product.total_revenue)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Department Sales */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <MapPin className="w-5 h-5 text-orange-600" />
            Ventas por Departamento
          </h3>
          <div className="h-80">
            {departmentSales.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={departmentSales} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis type="number" tick={{ fontSize: 12 }} />
                  <YAxis dataKey="department" type="category" width={100} tick={{ fontSize: 12 }} />
                  <Tooltip
                    formatter={(value: number) => formatCurrency(value)}
                    contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                  />
                  <Bar dataKey="total_revenue" fill={COLORS.accent} radius={[0, 8, 8, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-gray-400">
                No hay datos disponibles
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Category Sales */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Ventas por Categoría</h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                <th className="pb-3 px-4">Categoría</th>
                <th className="pb-3 px-4">Ingresos</th>
                <th className="pb-3 px-4">Productos</th>
                <th className="pb-3 px-4">Pedidos</th>
                <th className="pb-3 px-4">Promedio</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {categorySales.map((category, index) => (
                <tr key={category.category_id} className="hover:bg-gray-50">
                  <td className="py-4 px-4">
                    <div className="flex items-center gap-3">
                      <div
                        className="w-10 h-10 rounded-lg flex items-center justify-center"
                        style={{ backgroundColor: `${CHART_COLORS[index % CHART_COLORS.length]}20` }}
                      >
                        <span className="text-lg" style={{ color: CHART_COLORS[index % CHART_COLORS.length] }}>
                          {category.category_name.substring(0, 2)}
                        </span>
                      </div>
                      <span className="font-medium text-gray-900">{category.category_name}</span>
                    </div>
                  </td>
                  <td className="py-4 px-4 font-semibold text-gray-900">
                    {formatCurrency(category.total_revenue)}
                  </td>
                  <td className="py-4 px-4 text-gray-600">{category.product_count}</td>
                  <td className="py-4 px-4 text-gray-600">{category.order_count}</td>
                  <td className="py-4 px-4 text-gray-600">
                    {formatCurrency(category.total_revenue / category.order_count)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
